import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { IcoFolder, IcoAlertTriangle, IcoClose } from '../dashboard/DashIcons';

const REQUIRED = ['firstName', 'lastName', 'email'];
const VALID_ROLES = ['employee', 'teamlead', 'manager', 'adminassistant'];
const VALID_TYPES = ['fulltime', 'parttime', 'salaried'];

const COLUMN_ALIASES = {
  firstname:       'firstName',
  first_name:      'firstName',
  'first name':    'firstName',
  lastname:        'lastName',
  last_name:       'lastName',
  'last name':     'lastName',
  email:           'email',
  emailaddress:    'email',
  'email address': 'email',
  role:            'role',
  position:        'role',
  hourlyrate:      'hourlyRate',
  hourly_rate:     'hourlyRate',
  'hourly rate':   'hourlyRate',
  rate:            'hourlyRate',
  pay:             'hourlyRate',
  startdate:       'startDate',
  start_date:      'startDate',
  'start date':    'startDate',
  hiredate:        'startDate',
  'hire date':     'startDate',
  employmenttype:  'employmentType',
  employment_type: 'employmentType',
  'employment type': 'employmentType',
  type:            'employmentType',
};

function parseCSV(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  const parseRow = (line) => {
    const cells = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
        else { inQuotes = !inQuotes; }
      } else if (ch === ',' && !inQuotes) {
        cells.push(cur.trim()); cur = '';
      } else {
        cur += ch;
      }
    }
    cells.push(cur.trim());
    return cells;
  };

  const rawHeaders = parseRow(lines[0]);
  const headers = rawHeaders.map(h => {
    const key = h.toLowerCase().replace(/[^a-z0-9 _]/g, '').trim();
    return COLUMN_ALIASES[key] || h;
  });

  const rows = lines.slice(1).map(line => {
    const cells = parseRow(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = cells[i] || ''; });
    return obj;
  });

  return { headers, rows };
}

function validateRow(row, idx) {
  const errors = [];
  REQUIRED.forEach(f => { if (!row[f]?.trim()) errors.push(`Row ${idx + 1}: missing ${f}`); });
  if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) errors.push(`Row ${idx + 1}: invalid email`);
  if (row.role && !VALID_ROLES.includes(row.role)) errors.push(`Row ${idx + 1}: role must be one of ${VALID_ROLES.join(', ')}`);
  if (row.employmentType && !VALID_TYPES.includes(row.employmentType)) errors.push(`Row ${idx + 1}: employmentType must be one of ${VALID_TYPES.join(', ')}`);
  if (row.hourlyRate && isNaN(parseFloat(row.hourlyRate))) errors.push(`Row ${idx + 1}: hourlyRate must be a number`);
  return errors;
}

function downloadTemplate() {
  const csv = [
    'firstName,lastName,email,role,hourlyRate,startDate,employmentType',
    'Jane,Smith,jane@company.com,employee,18.00,2024-01-15,fulltime',
    'Bob,Jones,bob@company.com,manager,25.00,2023-06-01,fulltime',
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = 'employee_import_template.csv'; a.click();
  URL.revokeObjectURL(url);
}

export default function ImportEmployees() {
  const { authFetch } = useAuth();
  const navigate      = useNavigate();
  const fileRef       = useRef();

  const [rows, setRows]         = useState([]);
  const [headers, setHeaders]   = useState([]);
  const [errors, setErrors]     = useState([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [results, setResults]   = useState(null);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setResults(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers: h, rows: r } = parseCSV(ev.target.result);
      const allErrors = r.flatMap((row, i) => validateRow(row, i));
      setHeaders(h);
      setRows(r);
      setErrors(allErrors);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (errors.length > 0) return;
    setImporting(true);
    try {
      const res  = await authFetch('/api/employees/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employees: rows }),
      });
      const data = await res.json();
      setResults(data);
    } catch {
      setErrors(['Failed to connect to server.']);
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setRows([]); setHeaders([]); setErrors([]); setFileName(''); setResults(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
        <button
          onClick={() => navigate('/team')}
          style={{ padding: '0.5rem 1rem', background: '#9ca3af', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
        >
          ← Back
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: '800', color: '#111827' }}>Import Employees</h1>
          <p style={{ margin: '0.2rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Upload a CSV file to create employee profiles in bulk.</p>
        </div>
      </div>

      {/* Template download */}
      <div style={{ background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: '10px', padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <p style={{ margin: 0, fontWeight: '700', fontSize: '0.875rem', color: '#3730a3' }}>Need a template?</p>
          <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', color: '#4338ca' }}>
            Required columns: <strong>firstName, lastName, email</strong> &nbsp;·&nbsp; Optional: role, hourlyRate, startDate, employmentType
          </p>
        </div>
        <button
          onClick={downloadTemplate}
          style={{ padding: '0.55rem 1.1rem', background: '#22c55e', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontWeight: '700', fontSize: '0.82rem', whiteSpace: 'nowrap' }}
        >
          ⬇️ Download Template
        </button>
      </div>

      {/* File upload */}
      {!results && (
        <div
          style={{ background: 'white', border: '2px dashed #d1d5db', borderRadius: '12px', padding: '2.5rem', textAlign: 'center', marginBottom: '1.5rem', cursor: 'pointer' }}
          onClick={() => fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#22c55e'; }}
          onDragLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; }}
          onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#d1d5db'; const f = e.dataTransfer.files[0]; if (f) { fileRef.current.files = e.dataTransfer.files; handleFile({ target: { files: [f] } }); } }}
        >
          <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} style={{ display: 'none' }} />
          <IcoFolder size={40} color="#9ca3af"/>
          <p style={{ margin: '0 0 0.25rem', fontWeight: '700', color: '#111827' }}>{fileName || 'Click or drag a CSV file here'}</p>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#9ca3af' }}>Only .csv files are accepted</p>
        </div>
      )}

      {/* Validation errors */}
      {errors.length > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
          <p style={{ margin: '0 0 0.5rem', fontWeight: '700', color: '#991b1b', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}><IcoAlertTriangle size={14} color="#991b1b"/> Fix these issues before importing:</p>
          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {errors.map((e, i) => <li key={i} style={{ fontSize: '0.82rem', color: '#b91c1c', marginBottom: '0.2rem' }}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* Preview table */}
      {rows.length > 0 && !results && (
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '1.5rem' }}>
          <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ margin: 0, fontWeight: '700', color: '#111827', fontSize: '0.95rem' }}>Preview — {rows.length} employee{rows.length !== 1 ? 's' : ''}</p>
            <button onClick={reset} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><IcoClose size={13}/> Clear</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {headers.map(h => (
                    <th key={h} style={{ padding: '0.6rem 1rem', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                      {h}
                      {REQUIRED.includes(h) && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {headers.map(h => (
                      <td key={h} style={{ padding: '0.6rem 1rem', color: row[h] ? '#111827' : '#d1d5db' }}>
                        {row[h] || <em>—</em>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '1rem 1.25rem', borderTop: '1px solid #f3f4f6', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button onClick={reset} style={{ padding: '0.65rem 1.25rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={importing || errors.length > 0}
              style={{ padding: '0.65rem 1.5rem', background: errors.length > 0 ? '#d1d5db' : 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: errors.length > 0 ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '0.875rem' }}
            >
              {importing ? '⏳ Importing…' : `Import ${rows.length} Employee${rows.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {results && (
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <h2 style={{ margin: '0 0 1.25rem', fontSize: '1.1rem', fontWeight: '800', color: '#111827' }}>Import Complete</h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
            {[
              { label: 'Created',  count: results.created?.length  || 0, bg: '#d1fae5', color: '#065f46' },
              { label: 'Skipped', count: results.skipped?.length || 0, bg: '#fef3c7', color: '#92400e' },
              { label: 'Failed',  count: results.failed?.length  || 0, bg: '#fee2e2', color: '#991b1b' },
            ].map(s => (
              <div key={s.label} style={{ background: s.bg, borderRadius: '10px', padding: '1rem', textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: '1.75rem', fontWeight: '800', color: s.color }}>{s.count}</p>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', fontWeight: '600', color: s.color }}>{s.label}</p>
              </div>
            ))}
          </div>

          {results.skipped?.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ margin: '0 0 0.4rem', fontWeight: '700', fontSize: '0.82rem', color: '#92400e' }}>Skipped (email already in use):</p>
              {results.skipped.map((s, i) => <p key={i} style={{ margin: '0.15rem 0', fontSize: '0.8rem', color: '#78350f' }}>• {s.email}</p>)}
            </div>
          )}

          {results.failed?.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ margin: '0 0 0.4rem', fontWeight: '700', fontSize: '0.82rem', color: '#991b1b' }}>Failed:</p>
              {results.failed.map((f, i) => <p key={i} style={{ margin: '0.15rem 0', fontSize: '0.8rem', color: '#7f1d1d' }}>• {f.row?.email || 'unknown'} — {f.reason}</p>)}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
            <button onClick={reset} style={{ padding: '0.65rem 1.25rem', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>
              Import Another File
            </button>
            <button onClick={() => navigate('/team')} style={{ padding: '0.65rem 1.5rem', background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.875rem' }}>
              View Team Directory →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
